import { useState } from 'react'
import SelectBox from 'components/form/SelectBox'
import CurrencyInput from 'components/form/CurrencyInput'
import {
  BiddingStrategyTypes,
  ChangeAmountUnit,
  getRankOptionSimpleText,
  getRankOptionText,
  MaxBiddingAmountChangeTypes,
  RankOptions,
} from 'features/Codes'
import {
  boundary,
  ceil,
  decreaseByPercent,
  floor,
  increaseByPercent,
  invalidNumberInput,
  isPresent,
} from 'utils'
import { batch, useDispatch, useSelector } from 'react-redux'
import {
  reset,
  setBiddingStrategyType,
  setChangeAmount,
  setChangeAmountUnit,
  setErrors,
  setMaxBiddingAmount,
  setMaxBiddingAmountChangeType,
  setRank,
  setRankAmount,
  setStrategies,
  setUseRankAmount,
} from 'features/dashboard/operation/keywordBiddingStrategyEdit/keywordsBiddingStrategyEditSlice'
import Button from 'components/form/Button'
import useValidator from 'utils/hooks/useValidator'
import ErrorText from 'components/form/ErrorText'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import CheckBox from 'components/form/CheckBox'
import api from 'api/axios/foka'
import { failWithErrorResponse } from 'features/toast/Toast'
import useModifyBiddingStrategy from 'features/dashboard/operation/keywordBiddingStrategyEdit/useKeywordBiddingStrategyEdit'
import { useConfirm } from 'features/dialog/Confirm'
import { exitEditable } from 'features/dashboard/table/keyword/keywordTableSlice'

const KeywordBiddingStrategyEditTableHeader = () => {
  const dispatch = useDispatch()
  const { content, selectedIds } = useSelector(
    ({ dashboard }) => dashboard.table.keywordTable
  )
  const {
    strategies,
    changeAmount,
    maxBiddingAmount,
    maxBiddingAmountChangeType,
    biddingStrategyType,
    changeAmountUnit,
    rank,
    useRankAmount,
    rankAmount,
    isSubmitting: isKeywordBiddingStrategySubmitting,
    warnings: keywordBiddingStrategyWarnings,
  } = useSelector(state => state.dashboard.operation.keywordBiddingStrategy)
  const [isLoading, setLoading] = useState(false)
  const isCalculateType = [
    MaxBiddingAmountChangeTypes.INCREASE,
    MaxBiddingAmountChangeTypes.DECREASE,
  ].includes(maxBiddingAmountChangeType)

  const modifyBiddingStrategy = useModifyBiddingStrategy()
  const [ApplyBiddingStrategyConfirm, showApplyBiddingStrategyConfirm] =
    useConfirm()
  const keywordBiddingStrategyWarningsCount = Object.keys(
    keywordBiddingStrategyWarnings
  ).length

  const { validate, errors } = useValidator(
    () => ({
      maxBiddingAmount:
        biddingStrategyType !== BiddingStrategyTypes.INHERIT_FROM_ADGROUP &&
        maxBiddingAmountChangeType ===
          MaxBiddingAmountChangeTypes.NO_CALCULATE &&
        invalidNumberInput(maxBiddingAmount, 100000, 70, 10) &&
        '입찰가는 최소 70원, 최대 100,000원 범위 내에서 10원 단위로 등록 가능합니다.',
      changeAmount:
        isCalculateType &&
        changeAmountUnit === ChangeAmountUnit.WON &&
        changeAmount % 10 !== 0 &&
        '10원 단위로 등록 가능합니다.',
      rankAmount:
        maxBiddingAmountChangeType === MaxBiddingAmountChangeTypes.RANK &&
        useRankAmount &&
        invalidNumberInput(rankAmount, 100000, 70, 10) &&
        '최대 입찰가는 최소 70원, 최대 100,000원 범위 내에서 10원 단위로 등록 가능합니다.',
    }),
    [
      biddingStrategyType,
      maxBiddingAmountChangeType,
      maxBiddingAmount,
      isCalculateType,
      changeAmountUnit,
      changeAmount,
      useRankAmount,
      rankAmount,
    ]
  )

  const handleApplyStrategy = async () => {
    dispatch(setErrors({}))
    try {
      await validate()
    } catch (e) {
      return
    }
    let biddingAverages = []
    if (maxBiddingAmountChangeType === MaxBiddingAmountChangeTypes.RANK) {
      setLoading(true)
      const keywords = content
        .filter(({ id }) => selectedIds.includes(id))
        .map(({ text }) => text)
      try {
        const {
          data: { results },
        } = await api.recommendKeyword.getBiddingAverages(keywords)
        biddingAverages = results
      } catch (error) {
        failWithErrorResponse(error?.response?.data)
      }
      setLoading(false)
    }
    const applyValues = selectedIds.map(id => {
      const item = strategies.find(({ keywordId }) => keywordId === id)
      const result = { ...item }
      const keywordContent = content.find(
        ({ id: keywordId }) => keywordId === id
      )
      const currAmount =
        keywordContent.biddingStrategy.maxBiddingAmount ||
        keywordContent.parentDomainInfo.adGroup.maxBiddingAmount

      if (biddingStrategyType !== BiddingStrategyTypes.INHERIT_FROM_ADGROUP) {
        if (
          maxBiddingAmountChangeType ===
          MaxBiddingAmountChangeTypes.NO_CALCULATE
        ) {
          result.maxBiddingAmount = maxBiddingAmount
        } else if (
          maxBiddingAmountChangeType === MaxBiddingAmountChangeTypes.RANK
        ) {
          const biddingAverageAmount = biddingAverages
            ?.find(({ keyword }) => keyword === keywordContent.text)
            ?.[rank.device.toLowerCase()]?.find(
              biddingAverage => biddingAverage.rank === rank.rank
            )?.averageBiddingAmount
          result.maxBiddingAmount = biddingAverageAmount
            ? useRankAmount &&
              rankAmount !== '' &&
              !errors.rankAmount &&
              biddingAverageAmount > rankAmount
              ? Math.min(rankAmount, 100000)
              : biddingAverageAmount
            : 70
        } else if (changeAmountUnit === ChangeAmountUnit.PERCENTAGE) {
          result.maxBiddingAmount = boundary(
            maxBiddingAmountChangeType === MaxBiddingAmountChangeTypes.INCREASE
              ? increaseByPercent(currAmount, Number(changeAmount))
              : decreaseByPercent(currAmount, Number(changeAmount)),
            100000,
            70
          )
        } else if (changeAmountUnit === ChangeAmountUnit.WON) {
          result.maxBiddingAmount = boundary(
            maxBiddingAmountChangeType === MaxBiddingAmountChangeTypes.INCREASE
              ? ceil(currAmount + Number(changeAmount), -1)
              : floor(currAmount - Number(changeAmount), -1),
            100000,
            70
          )
        }
        result.biddingStrategyType =
          BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING
        result.maxBiddingAmountChangeType =
          MaxBiddingAmountChangeTypes.NO_CALCULATE
      } else {
        result.biddingStrategyType = BiddingStrategyTypes.INHERIT_FROM_ADGROUP
        result.maxBiddingAmountChangeType = undefined
        result.maxBiddingAmount = undefined
      }
      return result
    })
    dispatch(setStrategies(applyValues))
  }

  return (
    <>
      <div className="set_head">
        <div className="f_left">
          <div className="single_wrap">
            <SelectBox
              noBlurOnDefault
              className="opt_bidding"
              value={biddingStrategyType}
              onValueChange={v => dispatch(setBiddingStrategyType(v))}>
              <SelectBox.Item
                value={BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING}
                tiaraAction="키워드탭_입찰가변경_개별_고정입찰_클릭">
                고정입찰
              </SelectBox.Item>
              <SelectBox.Item
                value={BiddingStrategyTypes.INHERIT_FROM_ADGROUP}
                tiaraAction="키워드탭_입찰가변경_개별_기본입찰가_클릭">
                광고그룹의 기본입찰가
              </SelectBox.Item>
            </SelectBox>
          </div>
          {biddingStrategyType !==
            BiddingStrategyTypes.INHERIT_FROM_ADGROUP && (
            <>
              <div className="single_wrap">
                <SelectBox
                  className="opt_cate"
                  value={maxBiddingAmountChangeType}
                  onValueChange={v =>
                    dispatch(setMaxBiddingAmountChangeType(v))
                  }
                  noBlurOnDefault>
                  <SelectBox.Item
                    value={MaxBiddingAmountChangeTypes.NO_CALCULATE}
                    tiaraAction="키워드탭_입찰가변경_개별_직접입력_클릭">
                    직접입력
                  </SelectBox.Item>
                  <SelectBox.Item
                    value={MaxBiddingAmountChangeTypes.INCREASE}
                    tiaraAction="키워드탭_입찰가변경_개별_입찰가증액_클릭">
                    입찰가 증액
                    <HelpTooltip nowrap={false} tag="span">
                      증액/감액으로 계산된 입찰가 금액은 최소 70원 ~ 최대
                      100,000원 이내로 설정됩니다.
                    </HelpTooltip>
                  </SelectBox.Item>
                  <SelectBox.Item
                    value={MaxBiddingAmountChangeTypes.DECREASE}
                    tiaraAction="키워드탭_입찰가변경_개별_입찰가감액_클릭">
                    입찰가 감액
                    <HelpTooltip nowrap={false} tag="span">
                      증액/감액으로 계산된 입찰가 금액은 최소 70원 ~ 최대
                      100,000원 이내로 설정됩니다.
                    </HelpTooltip>
                  </SelectBox.Item>
                  <SelectBox.Item
                    value={MaxBiddingAmountChangeTypes.RANK}
                    tiaraAction="키워드탭_입찰가변경_개별_순위별평균_클릭">
                    순위별 평균 입찰가
                    <HelpTooltip nowrap={false} tag="span">
                      순위별 평균 입찰가는 프리미엄링크 캠페인 &gt; 검색매체
                      &gt; 카카오지면을 통해 광고가 집행된 키워드의 최근
                      30일(어제로부터 과거 30일까지) 과거 실적을 토대로 예측한
                      수치며 제공되는 데이터로 인한 광고 성과를 보장하지
                      않습니다. 순위별 평균 입찰가가 희망하는 입찰가를 초과하지
                      않도록 최대 입찰가를 설정할 수 있습니다.
                    </HelpTooltip>
                  </SelectBox.Item>
                </SelectBox>
              </div>
              {maxBiddingAmountChangeType ===
                MaxBiddingAmountChangeTypes.NO_CALCULATE && (
                <div className="single_wrap">
                  <CurrencyInput
                    type="원"
                    value={maxBiddingAmount || ''}
                    style={{ minWidth: 163 }}
                    min={70}
                    max={100000}
                    step={10}
                    maxLength={6}
                    onValueChange={v => dispatch(setMaxBiddingAmount(v))}
                  />
                  {errors.maxBiddingAmount && (
                    <div className="dash_error">
                      <ErrorText tag="p" error={errors.maxBiddingAmount} />
                    </div>
                  )}
                </div>
              )}
              {isCalculateType && (
                <>
                  <div className="single_wrap">
                    <SelectBox
                      value={changeAmountUnit}
                      onValueChange={v => {
                        batch(() => {
                          dispatch(setChangeAmountUnit(v))
                          dispatch(setChangeAmount(null))
                        })
                      }}
                      noBlurOnDefault>
                      <SelectBox.Item value={ChangeAmountUnit.PERCENTAGE}>
                        %
                      </SelectBox.Item>
                      <SelectBox.Item value={ChangeAmountUnit.WON}>
                        원
                      </SelectBox.Item>
                    </SelectBox>
                  </div>
                  <div className="single_wrap">
                    {changeAmountUnit === ChangeAmountUnit.PERCENTAGE ? (
                      <CurrencyInput
                        type="%"
                        style={{ minWidth: 163 }}
                        min={0}
                        step={1}
                        maxLength={3}
                        max={
                          maxBiddingAmountChangeType ===
                          MaxBiddingAmountChangeTypes.DECREASE
                            ? 100
                            : 999
                        }
                        error={errors.changeAmount}
                        value={changeAmount || ''}
                        onValueChange={v => dispatch(setChangeAmount(v))}
                      />
                    ) : (
                      <CurrencyInput
                        type="원"
                        style={{ minWidth: 163 }}
                        max={100000}
                        step={10}
                        maxLength={6}
                        error={errors.changeAmount}
                        value={changeAmount || ''}
                        onValueChange={v => dispatch(setChangeAmount(v))}
                      />
                    )}
                    {errors.changeAmount && (
                      <div className="dash_error">
                        <ErrorText tag="p" error={errors.changeAmount} />
                      </div>
                    )}
                  </div>
                </>
              )}
              {maxBiddingAmountChangeType ===
                MaxBiddingAmountChangeTypes.RANK && (
                <>
                  <div className="single_wrap">
                    <SelectBox
                      className="opt_ranking"
                      noBlurOnDefault
                      value={rank}
                      onValueChange={v => dispatch(setRank(v))}>
                      {RankOptions.map(item => (
                        <SelectBox.Item
                          key={`${item.device}${item.rank}`}
                          value={item}
                          tiaraAction={`키워드탭_입찰가변경_개별_순위별평균_${getRankOptionSimpleText(
                            item
                          )}위_클릭`}>
                          {getRankOptionText(item)}
                        </SelectBox.Item>
                      ))}
                    </SelectBox>
                  </div>
                  <div className="single_wrap">
                    <CheckBox
                      label="최대입찰가"
                      checked={useRankAmount}
                      onCheckedChange={v => dispatch(setUseRankAmount(v))}
                    />
                  </div>
                  <div className="single_wrap">
                    <CurrencyInput
                      disabled={!useRankAmount}
                      min={70}
                      max={100000}
                      step={10}
                      maxLength={6}
                      error={errors.rankAmount}
                      style={{ minWidth: 163 }}
                      value={rankAmount}
                      onChange={({ target: { value } }) =>
                        dispatch(setRankAmount(value))
                      }
                    />
                    {errors.rankAmount && (
                      <div className="dash_error">
                        <ErrorText tag="p" error={errors.rankAmount} />
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
          <div className="single_wrap">
            <Button
              line
              disabled={
                biddingStrategyType ===
                  BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING &&
                (maxBiddingAmountChangeType ===
                MaxBiddingAmountChangeTypes.NO_CALCULATE
                  ? !isPresent(maxBiddingAmount)
                  : isCalculateType
                  ? !isPresent(changeAmount)
                  : useRankAmount && !isPresent(rankAmount))
              }
              onClick={handleApplyStrategy}
              loading={isLoading}>
              적용
            </Button>
          </div>
          <div className="btn_group">
            <div className="single_wrap">
              <Button
                line
                onClick={() => {
                  batch(() => {
                    dispatch(exitEditable())
                    dispatch(reset())
                  })
                }}>
                이전
              </Button>
            </div>
            <div className="single_wrap">
              <Button
                primary
                disabled={isKeywordBiddingStrategySubmitting}
                onClick={async () => {
                  let validateErrors
                  try {
                    await modifyBiddingStrategy.validateErrors()
                  } catch (error) {
                    validateErrors = error
                  }
                  if (validateErrors === undefined) {
                    try {
                      await modifyBiddingStrategy.validateWarnings()
                    } catch (ignore) {}
                    showApplyBiddingStrategyConfirm()
                  }
                }}>
                저장
              </Button>
            </div>
          </div>
        </div>
      </div>
      <ApplyBiddingStrategyConfirm
        title="입찰가 변경"
        onClose={({ isConfirmed }) => {
          if (isConfirmed) {
            modifyBiddingStrategy.apply()
          }
        }}>
        {keywordBiddingStrategyWarningsCount > 0 ? (
          <>
            <em className="fc_warn2">
              변경 전 금액 대비 증가폭이 큰 입력값이{' '}
              {keywordBiddingStrategyWarningsCount}개 존재합니다.
            </em>
            <br />
            증가폭이 큰 키워드를 테이블 내 표시하였으며,
            <br />
            취소 버튼 클릭 시 확인할 수 있습니다.
            <br />
            <br />
            현재 입력값으로 입찰가를 수정하시겠습니까?
          </>
        ) : (
          '입찰가를 수정하시겠습니까?'
        )}
      </ApplyBiddingStrategyConfirm>
    </>
  )
}
export default KeywordBiddingStrategyEditTableHeader
